import React from 'react';
import { CheckCircle, AlertTriangle, AlertOctagon, Clock, Activity, HelpCircle, LucideIcon } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { MovementInsight } from '../../../../types/balanceComparison';

interface MovementInsightBadgeProps {
  insight: MovementInsight;
}

interface BadgeConfig {
  Icon: LucideIcon;
  className: string;
  labelKey: string;
}

const insightConfig: Record<MovementInsight, BadgeConfig> = {
  InSync: {
    Icon: CheckCircle,
    className: 'text-green-600 dark:text-green-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.inSync',
  },
  ErpLagging: {
    Icon: Clock,
    className: 'text-yellow-600 dark:text-yellow-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.erpLagging',
  },
  WmsMissingMovement: {
    Icon: AlertOctagon,
    className: 'text-red-600 dark:text-red-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.wmsMissingMovement',
  },
  QuietDrift: {
    Icon: AlertTriangle,
    className: 'text-red-600 dark:text-red-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.quietDrift',
  },
  ActiveMovement: {
    Icon: Activity,
    className: 'text-blue-600 dark:text-blue-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.activeMovement',
  },
  NoWmsMovement: {
    Icon: HelpCircle,
    className: 'text-gray-500 dark:text-gray-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.noWmsMovement',
  },
  NoErpMovement: {
    Icon: HelpCircle,
    className: 'text-gray-500 dark:text-gray-400',
    labelKey: 'warehouse.modules.balanceComparison.insight.noErpMovement',
  },
};

/**
 * Small badge rendering the auto-tolket bevegelses-vurdering for a row.
 * Tooltip = translated label so the manager understands the reasoning.
 */
export const MovementInsightBadge: React.FC<MovementInsightBadgeProps> = ({ insight }) => {
  const { t } = useTranslation();
  const cfg = insightConfig[insight] ?? insightConfig.NoErpMovement;
  const label = t(cfg.labelKey);
  const { Icon } = cfg;

  return (
    <span
      className={`inline-flex items-center gap-1 ${cfg.className}`}
      title={label}
      aria-label={label}
    >
      <Icon size={16} />
      <span className="text-xs hidden md:inline">{label}</span>
    </span>
  );
};
